import React, { useCallback, useEffect, useRef, useState } from 'react';
import { trackSnakeGameOver, trackSnakeGameStart } from '../Analytics/Analytics';
import './HomeSnakeGame.css';

const COLS = 22;
const ROWS = 14;
const CELL = 20;
const CANVAS_W = COLS * CELL;
const CANVAS_H = ROWS * CELL;
const TICK_MS = 115;
const START_FACING = { x: 1, y: 0 };

function randomFood(snake) {
  const free = [];
  for (let y = 0; y < ROWS; y += 1) {
    for (let x = 0; x < COLS; x += 1) {
      if (!snake.some((s) => s.x === x && s.y === y)) {
        free.push({ x, y });
      }
    }
  }
  if (free.length === 0) {
    return { x: 0, y: 0 };
  }
  return free[Math.floor(Math.random() * free.length)];
}

function opposite(a, b) {
  return a.x === -b.x && a.y === -b.y;
}

const HomeSnakeGame = ({ darkMode }) => {
  const canvasRef = useRef(null);
  const gameRef = useRef(null);
  const tickRef = useRef(null);
  const [score, setScore] = useState(0);
  const [phase, setPhase] = useState('idle');
  const [reducedMotion, setReducedMotion] = useState(false);

  const initState = useCallback(() => {
    const y = Math.floor(ROWS / 2);
    const x = Math.floor(COLS / 2);
    const snake = [
      { x, y },
      { x: x - 1, y },
      { x: x - 2, y },
    ];
    return {
      snake,
      food: randomFood(snake),
    };
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const g = gameRef.current;
    if (!canvas || !g) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const bg = darkMode ? '#0f172a' : '#e2e8f0';
    const grid = darkMode ? '#1e293b' : '#cbd5e1';
    const head = darkMode ? '#38bdf8' : '#0369a1';
    const body = darkMode ? '#22d3ee' : '#0ea5e9';
    const food = darkMode ? '#fbbf24' : '#d97706';

    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    ctx.strokeStyle = grid;
    ctx.lineWidth = 1;
    for (let c = 0; c <= COLS; c += 1) {
      ctx.beginPath();
      ctx.moveTo(c * CELL + 0.5, 0);
      ctx.lineTo(c * CELL + 0.5, CANVAS_H);
      ctx.stroke();
    }
    for (let r = 0; r <= ROWS; r += 1) {
      ctx.beginPath();
      ctx.moveTo(0, r * CELL + 0.5);
      ctx.lineTo(CANVAS_W, r * CELL + 0.5);
      ctx.stroke();
    }

    g.snake.forEach((seg, i) => {
      ctx.fillStyle = i === 0 ? head : body;
      const pad = 1;
      ctx.fillRect(seg.x * CELL + pad, seg.y * CELL + pad, CELL - pad * 2, CELL - pad * 2);
    });

    ctx.fillStyle = food;
    const fx = g.food.x * CELL + 4;
    const fy = g.food.y * CELL + 4;
    ctx.beginPath();
    ctx.arc(fx + (CELL - 8) / 2, fy + (CELL - 8) / 2, (CELL - 8) / 2, 0, Math.PI * 2);
    ctx.fill();
  }, [darkMode]);

  const stopLoop = useCallback(() => {
    if (tickRef.current) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const endGame = useCallback(() => {
    const g = gameRef.current;
    const finalScore =
      g && Array.isArray(g.snake) ? Math.max(0, g.snake.length - 3) : 0;
    trackSnakeGameOver(finalScore);
    stopLoop();
    setPhase('over');
  }, [stopLoop]);

  const tick = useCallback(() => {
    const g = gameRef.current;
    if (!g) return;

    if (!opposite(g.pending, g.dir)) {
      g.dir = g.pending;
    }

    const head = g.snake[0];
    const next = { x: head.x + g.dir.x, y: head.y + g.dir.y };

    if (next.x < 0 || next.x >= COLS || next.y < 0 || next.y >= ROWS) {
      endGame();
      return;
    }
    if (g.snake.some((s) => s.x === next.x && s.y === next.y)) {
      endGame();
      return;
    }

    const ate = next.x === g.food.x && next.y === g.food.y;
    const newSnake = [next, ...g.snake];
    if (!ate) {
      newSnake.pop();
    } else {
      g.food = randomFood(newSnake);
      setScore((s) => s + 1);
    }
    g.snake = newSnake;
    draw();
  }, [draw, endGame]);

  const startOrRestart = useCallback(
    (dirOverride) => {
      stopLoop();
      const base = initState();
      let d = START_FACING;
      if (dirOverride && !opposite(dirOverride, START_FACING)) {
        d = dirOverride;
      }
      gameRef.current = {
        ...base,
        dir: d,
        pending: d,
      };
      setScore(0);
      setPhase('playing');
      trackSnakeGameStart();
      draw();
      tickRef.current = window.setInterval(tick, TICK_MS);
    },
    [draw, initState, stopLoop, tick]
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReducedMotion(mq.matches);
    setReducedMotion(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    gameRef.current = {
      ...initState(),
      dir: START_FACING,
      pending: START_FACING,
    };
    draw();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo tablero inicial al montar
  }, []);

  useEffect(() => {
    if (!gameRef.current) return;
    draw();
  }, [darkMode, draw]);

  useEffect(() => {
    return () => stopLoop();
  }, [stopLoop]);

  const queueDir = useCallback(
    (d) => {
      if (reducedMotion) return;
      const g = gameRef.current;
      if (!g) return;
      if (phase === 'idle') {
        if (opposite(d, START_FACING)) return;
        startOrRestart(d);
        return;
      }
      if (phase !== 'playing') return;
      if (opposite(d, g.dir)) return;
      g.pending = d;
    },
    [phase, reducedMotion, startOrRestart]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (reducedMotion) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
        return;
      }
      const key = e.key;
      if (key === 'ArrowUp') {
        e.preventDefault();
        queueDir({ x: 0, y: -1 });
      } else if (key === 'ArrowDown') {
        e.preventDefault();
        queueDir({ x: 0, y: 1 });
      } else if (key === 'ArrowLeft') {
        e.preventDefault();
        queueDir({ x: -1, y: 0 });
      } else if (key === 'ArrowRight') {
        e.preventDefault();
        queueDir({ x: 1, y: 0 });
      } else if (key === ' ' || key === 'Enter') {
        if (phase === 'over') {
          e.preventDefault();
          startOrRestart();
        } else if (phase === 'idle') {
          e.preventDefault();
          startOrRestart();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, queueDir, reducedMotion, startOrRestart]);

  const sectionClass = `home-snake-section ${darkMode ? 'dark' : 'light'}`;

  if (reducedMotion) {
    return (
      <section className={sectionClass} aria-label="Minijuego oculto por preferencia de movimiento">
        <div className="home-snake-inner">
          <div className="home-snake-head">
            <p className="home-snake-kicker">Te cuidamos la vista (y los nervios)</p>
            <h2 className="home-snake-title">La serpiente se queda dormida</h2>
          </div>
          <p className="home-snake-static">
            Tu equipo pide menos movimiento en pantalla y nos parece bien. Todo lo demás de la
            página sigue igual; solo hemos apagado el minijuego para que nada parpadee ni corra sin
            necesidad.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className={sectionClass} aria-label="Minijuego serpiente, solo por diversión">
      <div className="home-snake-inner">
        <div className="home-snake-head">
          <p className="home-snake-kicker">Gracias por bajar hasta el fondo</p>
          <h2 className="home-snake-title">Regalo de scroll: una serpiente con buen rollo</h2>
          <p className="home-snake-desc">
            Si has leído hasta aquí, ya nos has hecho un favor enorme. Son dos minutos sin reuniones
            ni complicaciones de por medio: una pausa tonta para despejarte, recoger bolitas, esquivar
            paredes y no enredarte la cola. No es un truco ni un formulario disfrazado: solo un guiño
            con cariño para quien se toma en serio leer las cosas hasta el final.
          </p>
        </div>

        <div className="home-snake-wrap">
          <canvas
            ref={canvasRef}
            className="home-snake-canvas"
            width={CANVAS_W}
            height={CANVAS_H}
            tabIndex={0}
            role="img"
            aria-label="Serpiente: flechas para moverte por el tablero."
            onClick={() => canvasRef.current?.focus()}
          />
        </div>

        <div className="home-snake-meta">
          <span className="home-snake-score">
            Bolitas en esta partida: <strong>{score}</strong>
          </span>
          {phase === 'over' && (
            <button type="button" className="home-snake-btn" onClick={() => startOrRestart()}>
              Otra ronda
            </button>
          )}
          {phase === 'idle' && (
            <button type="button" className="home-snake-btn" onClick={() => startOrRestart()}>
              Jugar una ronda
            </button>
          )}
        </div>

        <div className="home-snake-dpad">
          <span className="dpad-empty" />
          <button type="button" onClick={() => queueDir({ x: 0, y: -1 })} aria-label="Arriba">
            ↑
          </button>
          <span className="dpad-empty" />
          <button type="button" onClick={() => queueDir({ x: -1, y: 0 })} aria-label="Izquierda">
            ←
          </button>
          <span className="dpad-empty" />
          <button type="button" onClick={() => queueDir({ x: 1, y: 0 })} aria-label="Derecha">
            →
          </button>
          <span className="dpad-empty" />
          <button type="button" onClick={() => queueDir({ x: 0, y: 1 })} aria-label="Abajo">
            ↓
          </button>
          <span className="dpad-empty" />
        </div>

        <p className="home-snake-hint">
          {phase === 'idle' &&
            'Puedes arrancar con el botón o con las flechas. Si el teclado no responde, un clic en el tablero suele arreglarlo.'}
          {phase === 'playing' &&
            '¿Nada se mueve? Haz clic en el cuadro: a veces el foco se ha quedado en otro sitio de la página.'}
          {phase === 'over' &&
            'Fin de la partida. Enter o «Otra ronda» si te apetece un intento más antes de irte.'}
        </p>
      </div>
    </section>
  );
};

export default HomeSnakeGame;
